/** Translate the modal's graph-space edit intents into the draft's
 *  `pending_dep_edits` ops. Pure — the host calls these in a setState updater,
 *  then re-fetches the cascaded graph.
 *
 *  The modal speaks graph ids (a real activity id as a string, OR a draft_id
 *  for a pending new activity). An edge op references a committed edge by its
 *  `edge_id` (remove / update), or carries endpoint refs for an add. Endpoint
 *  refs are `number` for committed activities, `string` (the draft_id) for
 *  pending new activities — publish remaps the latter.
 */
import type { DepEditOp } from "../../../models";
import {
  predTypeFromShort,
  type DepGraph,
  type DepGraphPredType,
} from "../../../models";

/** Resolve a graph id to an edge-op endpoint ref: the draft_id (string) for a
 *  pending new activity, else the numeric activity id. */
function endpointRef(graph: DepGraph, id: string): number | string {
  const node = graph.activities[id];
  if (node && node.is_new) return id;
  // A draft id the graph hasn't picked up yet (e.g. a bar dropped on the board
  // moments ago) must stay a string ref — Number() would stage NaN→null, an op
  // the backend drops as malformed.
  const n = Number(id);
  return Number.isFinite(n) ? n : id;
}

/** The committed edge_id for the predId -> succId edge, or null if the edge is
 *  draft-added (a SimEdge stand-in) or absent. */
function committedEdgeId(graph: DepGraph, succId: string, predId: string): number | null {
  for (const e of graph.edges[succId] ?? []) {
    if (e.pred_id === predId) return e.edge_id;
  }
  return null;
}

/** True when the predId -> succId edge exists in the graph as a draft add. */
function isDraftEdge(graph: DepGraph, succId: string, predId: string): boolean {
  for (const e of graph.edges[succId] ?? []) {
    if (e.pred_id === predId) return e.is_new && e.edge_id == null;
  }
  return false;
}

/** Does this draft `add` op describe the predId -> succId edge? Compares the
 *  op's endpoint refs against the graph ids (numeric refs stringify back). */
function addMatches(
  op: Extract<DepEditOp, { op: "add" }>,
  predId: string,
  succId: string,
): boolean {
  return String(op.pred_activity_id) === predId && String(op.succ_activity_id) === succId;
}

/** Append an add op (default FS, 0 lag) for predId -> succId. */
export function addEdge(
  edits: DepEditOp[],
  graph: DepGraph,
  predId: string,
  succId: string,
  type: DepGraphPredType = "FS",
  lagDays = 0,
): DepEditOp[] {
  const exists = edits.some(
    (e) => e.op === "add" && addMatches(e, predId, succId),
  );
  if (exists) return edits;
  return [
    ...edits,
    {
      op: "add",
      pred_activity_id: endpointRef(graph, predId),
      succ_activity_id: endpointRef(graph, succId),
      pred_type: predTypeFromShort(type),
      lag_hours: lagDays * 8,
    },
  ];
}

/** Drop the predId -> succId edge: stage a `remove` for a committed edge, or
 *  drop the matching `add` op for a draft-added edge. */
export function removeEdge(
  edits: DepEditOp[],
  graph: DepGraph,
  succId: string,
  predId: string,
): DepEditOp[] {
  if (isDraftEdge(graph, succId, predId)) {
    return edits.filter(
      (e) => !(e.op === "add" && addMatches(e, predId, succId)),
    );
  }
  const edgeId = committedEdgeId(graph, succId, predId);
  if (edgeId == null) return edits;
  // Replace any prior update for this edge with the remove; the edge is gone.
  const out = edits.filter(
    (e) => !((e.op === "update" || e.op === "remove") && e.edge_id === edgeId),
  );
  out.push({ op: "remove", edge_id: edgeId });
  return out;
}

/** Retype / re-lag the predId -> succId edge. Committed edge -> an `update`
 *  op (replacing any prior update for it). Draft-added edge -> patch the
 *  matching `add` op in place. */
export function retypeEdge(
  edits: DepEditOp[],
  graph: DepGraph,
  succId: string,
  predId: string,
  type: DepGraphPredType,
  lagDays: number,
): DepEditOp[] {
  const predTypeWire = predTypeFromShort(type);
  const lagHours = lagDays * 8;

  if (isDraftEdge(graph, succId, predId)) {
    return edits.map((e) =>
      e.op === "add" && addMatches(e, predId, succId)
        ? { ...e, pred_type: predTypeWire, lag_hours: lagHours }
        : e,
    );
  }
  const edgeId = committedEdgeId(graph, succId, predId);
  if (edgeId == null) return edits;
  const out = edits.filter(
    (e) => !(e.op === "update" && e.edge_id === edgeId),
  );
  out.push({
    op: "update",
    edge_id: edgeId,
    pred_type: predTypeWire,
    lag_hours: lagHours,
  });
  return out;
}


/** True when adding pred -> succ would close a dependency loop: walk DOWNSTREAM
 *  from `succId` over the graph's edges (which already include staged edits);
 *  reaching `predId` means pred already depends on succ. */
export function wouldCreateCycle(
  graph: DepGraph,
  predId: string,
  succId: string,
  /** Edges already accepted in the SAME batch (e.g. a chain) — a loop can
   *  need several of them plus committed ties to close. */
  extraEdges?: Array<{ pred: string; succ: string }>,
): boolean {
  if (predId === succId) return true;
  const succsOf = new Map<string, string[]>();
  for (const [sid, edges] of Object.entries(graph.edges)) {
    for (const edge of edges) {
      const list = succsOf.get(edge.pred_id);
      if (list) list.push(sid);
      else succsOf.set(edge.pred_id, [sid]);
    }
  }
  for (const ex of extraEdges ?? []) {
    const list = succsOf.get(ex.pred);
    if (list) list.push(ex.succ);
    else succsOf.set(ex.pred, [ex.succ]);
  }
  const seen = new Set<string>([succId]);
  const stack = [succId];
  while (stack.length > 0) {
    const cur = stack.pop() as string;
    for (const nxt of succsOf.get(cur) ?? []) {
      if (nxt === predId) return true;
      if (!seen.has(nxt)) {
        seen.add(nxt);
        stack.push(nxt);
      }
    }
  }
  return false;
}
